import { useQuery } from '@apollo/client';
import { get } from 'lodash';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { BreadcrumbJsonLd, CarouselJsonLd, CollectionPageJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';
import { useEffect, useMemo } from 'react';
import SEOTags from '~/components/SEOTags';
import { PATH, SectionsKey } from '~/constants/enum';
import { GET_AGGREGATIONS, GET_META_CATEGORY_BY_SLUG, GET_PRODUCTS_BY_CATEGORY_RESOLVER } from '~/data/categories';
import {
    SEOCategory,
    SEOCategoryResponse,
    SEOContentCategory,
    SEOContentCategoryResponse,
    SEOReviewCategory
} from '~/data/categories/interface';
import { QUERY_OPTIMISTIC_PRODUCTS_LIST } from '~/data/product/gql';
import { useGetFilterValues } from '~/hooks/useGetFilterValues';
import Layout from '~/layouts/ThemeLayout';
import { ProductListProps } from '~/sections/ProductList/ProductList';
import { trackingCategory } from '~/services/tracking';
import { initializeApollo } from '~/utils/apollo-client';
import { converShortCodeFilterKey, convertCleanFilterKey } from '~/utils/converters';
import { createSortObject } from '~/utils/filter';
import { getPageStruct, renderSectionNew } from '~/utils/pageTwo';
import { getTheme, getUrlByTheme } from '~/utils/theme';
import { transformProductItems } from '~/utils/transform';

const pageStruct = getPageStruct(getTheme(), 'category-detail');

const sections: any = pageStruct?.children
    ?.sort((a: any, b: any) => a.order - b.order)
    .map((item: any) => {
        const Section = renderSectionNew(item);

        return {
            id: item.id,
            order: item.order,
            dataKey: item.dataKey,
            component: !!Section ? <Section /> : null
        };
    });

const CategoryDetailPage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const {
        slug,
        dataProductItems,
        dehydratedDataReviews,
        initialApolloState,
        metaCategories,
        categoryId,
        categoryName
    } = props;
    const router = useRouter();
    const { query } = router;
    //Filter Values
    const { filterValues, pageSize = 40, currentPage = 1, sort } = useGetFilterValues();

    const pagination = {
        pageSize: pageSize,
        currentPage
    };

    // //Category ID
    // const categoryName = useMemo(() => {
    //   if (!data) return null;
    //   return data?.categories?.items?.[0]?.name;
    // }, [data]);

    //Aggregations
    const { data: filters } = useQuery<{ products: FilterType }>(GET_AGGREGATIONS, {
        variables: {
            filter: {
                category_id: {
                    eq: categoryId
                }
            }
        },
        skip: !categoryId
    });
    const { aggregations, sort_fields } = filters?.products || {};

    //Products

    const { data: basicProductData, loading } = useQuery(QUERY_OPTIMISTIC_PRODUCTS_LIST, {
        variables: {
            ...pagination,
            filter: {
                ...convertCleanFilterKey(filterValues, aggregations?.map((i: any) => i?.attribute_code) || []),
                category_id: {
                    eq: categoryId ? categoryId : []
                }
            },
            sort: sort ?? { best_sales: 'DESC' }
        },
        onError: ({ message }) => console.error(message, 'QUERY_SEARCH_BY_KEYWORD_GQL'),
        skip: !categoryId
    });

    const productItems = useMemo(() => {
        return transformProductItems(get(basicProductData, 'products.items', []));
    }, [basicProductData]);
    const filterData = useMemo(() => aggregations || [], [aggregations]);
    const sortFields = useMemo(() => sort_fields, [sort_fields]);

    const sectionData = useMemo(() => {
        return {
            [SectionsKey.ProductList]: {
                breadcrumbs: [{ title: 'Home', path: '/' }].concat(
                    metaCategories?.breadcrumbs?.map((c: any) => {
                        const path = metaCategories.url_path?.split(c.category_url_key)[0];
                        return {
                            title: c.category_name,
                            path: getUrlByTheme(PATH.CategoryDetail.replace('[slug]', path + c.category_url_key))
                        };
                    }) || []
                ),
                title: categoryName,
                filters: {
                    aggregations: filterData,
                    sortFields: sortFields
                },
                defaultSort: 'best_sales-DESC',
                data: productItems,
                total: basicProductData?.products?.total_count || 0,
                isLoading: loading,
                isShowFilter: query?.filter === 'show',
                identifier: 'category',
                listKey: categoryId
            } as ProductListProps
        };
    }, [
        productItems,
        categoryName,
        filterData,
        filterValues,
        sortFields,
        basicProductData?.products?.total_count,
        loading,
        sort,
        categoryId
    ]);

    const { meta_title, meta_description, meta_keywords, url_key, name } = metaCategories;
    const dataCarouselJsonLd = dataProductItems?.map(({ url_key, name, thumbnail }: any) => {
        return {
            keywords: url_key,
            url: getUrlByTheme(PATH.ProductDetail.replace('[slug]', url_key)),
            thumbnailUrl: thumbnail?.url,
            image: thumbnail?.url,
            name,
            about: name
        };
    });
    const itemListElements = [
        {
            position: 1,
            name: 'Home',
            item: process.env.NEXT_PUBLIC_URL + ''
        },
        {
            position: 2,
            name: meta_title,
            item: getUrlByTheme(PATH.CategoryDetail.replace('[slug]', url_key))
        }
    ];

    useEffect(() => {
        trackingCategory({
            keyword: slug,
            data: productItems
        });
    }, [url_key]);
    return (
        <Layout>
            <SEOTags
                title={meta_title || name}
                description={meta_description || name}
                keywords={meta_keywords || name}
                url={getUrlByTheme(PATH.CategoryDetail.replace('[slug]', url_key))}
                type="product.group"
            />
            {!!dataCarouselJsonLd?.length && (
                <CollectionPageJsonLd name={meta_title || name} hasPart={dataCarouselJsonLd} />
            )}

            {metaCategories && <BreadcrumbJsonLd itemListElements={itemListElements} />}
            {!!dataCarouselJsonLd.length && <CarouselJsonLd ofType="default" data={dataCarouselJsonLd} />}

            <div className="flex flex-col gap-10">
                {sections.map((section: any, index: number) => {
                    const Section = section.component;
                    return (
                        <React.Fragment key={`section-${index}`}>
                            {React.isValidElement(Section)
                                ? React.cloneElement(Section as React.ReactElement, {
                                      ...get(sectionData, section.dataKey, {})
                                  })
                                : Section}
                        </React.Fragment>
                    );
                })}
            </div>
        </Layout>
    );
};
export default CategoryDetailPage;
export async function getServerSideProps({ params, query }: GetServerSidePropsContext) {
    const slug = (params?.slug as string[]) || {};
    const apolloClient = initializeApollo({
        httpLinkProps: { useGETForQueries: true }
    });

    const metaData = {
        dataProductItems: [] as SEOContentCategory[],
        metaCategories: {} as SEOCategory,
        dehydratedDataReviews: {} as SEOReviewCategory,
        initialApolloState: apolloClient.cache.extract()
    };

    try {
        // const reviewData: SEOReviewCategoryResponse = await apolloClient.query({
        //   query: QUERY_REVIEW_BY_CATEGORY_SHORT,
        //   variables: {
        //     url_key: slug?.join("/") ,
        //     current_page: 1,
        //   },
        // });

        const resolver: any = await apolloClient.query({
            query: GET_PRODUCTS_BY_CATEGORY_RESOLVER,
            variables: {
                url: slug?.join('/') + '.html'
            }
        });

        // const tags = items?.[0] || {};
        const id = resolver?.data?.urlResolver.id;

        const metaCategoryResponse: SEOCategoryResponse = await apolloClient.query({
            query: GET_META_CATEGORY_BY_SLUG,
            variables: {
                id: id
            }
        });

        // const contents: SEOContentCategoryResponse = await apolloClient.query({
        //   query: GET_PRODUCTS_BY_CATEGORY_ID_FOR_RICH_CONTENT,
        //   variables: {
        //     category_id: id,
        //   },
        // });
        const filter = converShortCodeFilterKey(query as any, 'decode');
        const contents: SEOContentCategoryResponse = await apolloClient.query({
            query: QUERY_OPTIMISTIC_PRODUCTS_LIST,
            variables: {
                pageSize: query?.pageSize || 30,
                currentPage: query?.currentPage || 1,
                filter: convertCleanFilterKey(filter, {}) || {},
                category_id: {
                    eq: id ? id : ''
                },
                sort: createSortObject(query.sort as string, '-') || {
                    best_sales: 'DESC'
                },
                client: typeof window !== 'undefined' ? 'web' : 'server'
            }
        });

        metaData.metaCategories = metaCategoryResponse?.data?.categories?.items?.[0] || {};
        metaData.dataProductItems = contents?.data?.products?.items || [];
        // metaData.dehydratedDataReviews = reviewData?.data?.reviewCategory || {};
        return {
            props: {
                slug: slug?.join('/'),
                categoryId: id,
                categoryName: metaCategoryResponse?.data?.categories?.items?.[0]?.name || [],
                ...metaData
            }
        };
    } catch (error) {
        return {
            notFound: true
        };
    }
}
